// =====================================================================
// Stats Calculator - คำนวณ Stats แบบสมจริง (Flat + Percentage)
// =====================================================================

/**
 * กำหนดว่า stat ไหนเป็น flat, ไหนเป็น percentage
 * 
 * FLAT STATS: บวกตรงๆ เข้า base
 * - hp, phys_atk, magic_power, phys_def, magic_def, move_speed
 * - max_mana, armor_pierce, magic_pierce, hp_regen, mana_regen
 * 
 * PERCENTAGE STATS: บวกเป็น % ของ base (หรือเป็น % โดยตรง)
 * - attack_speed (% เพิ่มจาก base attack speed)
 * - crit_rate (% โอกาส crit)
 * - crit_dmg (% damage crit)
 * - life_steal (% ดูดเลือด)
 * - magic_life_steal (% ดูดเลือดเวท)
 * - cooldown_reduction (% ลด CD)
 * - armor_pierce_pct (% เจาะเกราะ)
 * - magic_pierce_pct (% เจาะเกราะเวท)
 * - phys_def_pct (% เพิ่ม def)
 * - magic_def_pct (% เพิ่ม magic def)
 * - move_speed_pct (% เพิ่มความเร็ว)
 * - hp_pct (% เพิ่ม HP)
 */

// Stats ที่เป็น percentage (บวกรวมกันแล้วคูณ base ทีเดียว)
const PERCENTAGE_STATS = new Set([
  'attack_speed',
  'crit_rate',
  'crit_dmg',
  'life_steal',
  'magic_life_steal',
  'cooldown_reduction',
  'armor_pierce_pct',
  'magic_pierce_pct',
  'spell_vamp',
]);

// Stats ที่เป็น % ของ base stat อื่น (ต้องคำนวณพิเศษ)
const PERCENTAGE_OF_BASE = {
  'hp_pct': 'hp',
  'phys_def_pct': 'phys_def',
  'magic_def_pct': 'magic_def',
  'move_speed_pct': 'move_speed',
  'phys_atk_pct': 'phys_atk',
  'magic_power_pct': 'magic_power',
};

// Stats ที่เป็น flat (บวกตรงๆ)
const FLAT_STATS = new Set([
  'hp',
  'phys_atk',
  'magic_power',
  'phys_def',
  'magic_def',
  'move_speed',
  'max_mana',
  'armor_pierce',
  'magic_pierce',
  'hp_regen',
  'mana_regen',
]);

/**
 * รวม stats จาก items ทั้งหมด
 * @param {Array} items - array ของ item objects
 * @returns {Object} { flat: {...}, pct: {...}, pctOfBase: {...} }
 */
export const sumItemStatsByType = (items = []) => {
  const result = {
    flat: {},      // stats ที่บวกตรงๆ
    pct: {},       // stats ที่เป็น % โดยตรง (เช่น crit_rate)
    pctOfBase: {}, // stats ที่เป็น % ของ base (เช่น hp_pct)
  };

  for (const item of items) {
    const stats = item?.stats || {};
    
    for (const [key, value] of Object.entries(stats)) {
      const v = +value || 0;
      if (v === 0) continue;

      // เช็คว่าเป็น % ของ base stat อื่นไหม
      if (PERCENTAGE_OF_BASE[key]) {
        result.pctOfBase[key] = (result.pctOfBase[key] || 0) + v;
      }
      // เช็คว่าเป็น percentage stat ไหม
      else if (PERCENTAGE_STATS.has(key)) {
        result.pct[key] = (result.pct[key] || 0) + v;
      }
      // ไม่ใช่ก็เป็น flat
      else {
        result.flat[key] = (result.flat[key] || 0) + v;
      }
    }
  }

  return result;
};

/**
 * คำนวณ total stats แบบสมจริง
 * 
 * สูตร:
 * 1. total = base + flat_bonus
 * 2. total = total * (1 + pct_of_base / 100)  [ถ้ามี]
 * 3. percentage stats จะแสดงเป็นค่า % โดยตรง (เช่น crit_rate: 25 หมายถึง 25%)
 * 
 * @param {Object} heroLevelObj - stats ของ hero ที่ level นั้น
 * @param {Array} items - array ของ item objects
 * @returns {Object} { base, flat, pctOfBase, pct, total }
 */
export const computeTotalStatsRealistic = (heroLevelObj, items = []) => {
  const base = { ...(heroLevelObj || {}) };
  const itemStats = sumItemStatsByType(items);
  
  const total = {};
  
  // รวม keys ทั้งหมด
  const allKeys = new Set([
    ...Object.keys(base),
    ...Object.keys(itemStats.flat),
    ...Object.keys(itemStats.pct),
    ...Object.keys(itemStats.pctOfBase),
  ]);

  for (const key of allKeys) {
    const baseVal = +base[key] || 0;
    const flatBonus = +itemStats.flat[key] || 0;
    const pctBonus = +itemStats.pct[key] || 0;

    // เช็คว่า key นี้มี pctOfBase ไหม (เช่น hp_pct → hp)
    const pctOfBaseKey = Object.entries(PERCENTAGE_OF_BASE)
      .find(([pctKey, baseKey]) => baseKey === key)?.[0];
    const pctOfBaseVal = pctOfBaseKey ? (+itemStats.pctOfBase[pctOfBaseKey] || 0) : 0;

    if (PERCENTAGE_STATS.has(key)) {
      // Percentage stats: รวม base + item % โดยตรง
      // เช่น crit_rate: hero มี 0%, item มี 25% → total = 25%
      total[key] = baseVal + pctBonus;
    } 
    else if (PERCENTAGE_OF_BASE[key]) {
      // pctOfBase key เอง (เช่น hp_pct) - เก็บไว้แต่ไม่ต้องคำนวณซ้ำ
      total[key] = baseVal + (+itemStats.pctOfBase[key] || 0);
    }
    else {
      // Flat stats: base + flat bonus, แล้วคูณ % ถ้ามี
      let val = baseVal + flatBonus;
      
      // ถ้ามี pctOfBase ให้คูณเพิ่ม
      if (pctOfBaseVal !== 0) {
        val = val * (1 + pctOfBaseVal / 100);
      }
      
      total[key] = val;
    }
  }

  // Handle pctOfBase keys ที่อาจไม่มีใน allKeys
  for (const [pctKey, baseKey] of Object.entries(PERCENTAGE_OF_BASE)) {
    if (itemStats.pctOfBase[pctKey] && !total[pctKey]) {
      total[pctKey] = +itemStats.pctOfBase[pctKey] || 0;
    }
  }

  return {
    base,
    flat: itemStats.flat,
    pct: itemStats.pct,
    pctOfBase: itemStats.pctOfBase,
    total,
  };
};

/**
 * คำนวณ damage จริงหลังหัก defense
 * สูตร ROV: damage = raw * 600 / (600 + defense)
 * 
 * ถ้ามี armor penetration:
 * - flat pen: defense = defense - flat_pen (min 0)
 * - % pen: defense = defense * (1 - pct_pen / 100)
 * 
 * @param {number} rawDamage - damage ก่อนหัก def
 * @param {number} defense - ค่า def ของเป้าหมาย
 * @param {number} flatPen - armor pierce แบบ flat
 * @param {number} pctPen - armor pierce แบบ %
 * @returns {number} damage หลังหัก def
 */
export const applyDefenseWithPenetration = (rawDamage, defense, flatPen = 0, pctPen = 0) => {
  let effectiveDef = Math.max(0, +defense || 0);
  
  // ลำดับการคำนวณใน ROV: % pen ก่อน, แล้วค่อย flat pen
  // (บางเกมใช้ flat ก่อน - ต้องเช็คจากเกมจริง)
  effectiveDef = effectiveDef * (1 - (+pctPen || 0) / 100);
  effectiveDef = Math.max(0, effectiveDef - (+flatPen || 0));
  
  return (+rawDamage || 0) * (600 / (600 + effectiveDef));
};

/**
 * คำนวณ raw damage ของ skill
 * @param {Object} params
 * @returns {number} raw damage
 */
export const computeSkillRawDamage = ({ 
  base = 0, 
  adScaling = 0, 
  apScaling = 0, 
  attackerStats 
}) => {
  const atk = +attackerStats?.phys_atk || 0;
  const ap = +attackerStats?.magic_power || 0;
  return (+base || 0) + (+adScaling || 0) * atk + (+apScaling || 0) * ap;
};

/**
 * คำนวณ DPS (damage per second) จาก auto attack
 * @param {Object} stats - total stats ของ hero
 * @param {string} damageType - 'phys' หรือ 'magic'
 * @returns {Object} { dps, perHit, hitsPerSec }
 */
export const calculateAutoDPS = (stats, damageType = 'phys') => {
  const baseAtk = damageType === 'phys' 
    ? (+stats.phys_atk || 0) 
    : (+stats.magic_power || 0);
  
  // Attack speed: base ~100%, items เพิ่มเป็น %
  // สมมติ base attack speed = 1 hit/sec, +100% = 2 hits/sec
  const atkSpeedBonus = +stats.attack_speed || 0;
  const hitsPerSec = 1 * (1 + atkSpeedBonus / 100);
  
  // Crit calculation
  const critRate = Math.min(100, +stats.crit_rate || 0);
  const critDmg = +stats.crit_dmg || 200; // default 200%
  
  // Average damage per hit including crit
  const critMultiplier = 1 + (critRate / 100) * (critDmg / 100 - 1);
  const perHit = baseAtk * critMultiplier;
  
  // DPS
  const dps = perHit * hitsPerSec;
  
  return { dps, perHit, hitsPerSec, critMultiplier };
};

/**
 * คำนวณ Effective HP (HP + Defense)
 * @param {Object} stats - total stats
 * @param {string} damageType - 'phys' หรือ 'magic'
 * @returns {number} effective HP
 */
export const calculateEffectiveHP = (stats, damageType = 'phys') => {
  const hp = +stats.hp || 0;
  const def = damageType === 'phys' 
    ? (+stats.phys_def || 0) 
    : (+stats.magic_def || 0);
  
  // EHP = HP * (1 + DEF/600)
  return hp * (1 + def / 600);
};

/**
 * คำนวณ Time To Kill (TTK)
 * @param {Object} attackerStats - stats ของผู้โจมตี
 * @param {Object} defenderStats - stats ของผู้ถูกโจมตี
 * @param {string} damageType - 'phys' หรือ 'magic'
 * @returns {Object} { ttk, dps, effectiveHP }
 */
export const calculateTTK = (attackerStats, defenderStats, damageType = 'phys') => {
  const { dps, perHit, hitsPerSec } = calculateAutoDPS(attackerStats, damageType);
  
  const def = damageType === 'phys' 
    ? (+defenderStats.phys_def || 0) 
    : (+defenderStats.magic_def || 0);
  
  const flatPen = damageType === 'phys'
    ? (+attackerStats.armor_pierce || 0)
    : (+attackerStats.magic_pierce || 0);
  
  const pctPen = damageType === 'phys'
    ? (+attackerStats.armor_pierce_pct || 0)
    : (+attackerStats.magic_pierce_pct || 0);
  
  const effectiveDamage = applyDefenseWithPenetration(perHit, def, flatPen, pctPen);
  const effectiveDPS = effectiveDamage * hitsPerSec;
  
  const defenderHP = +defenderStats.hp || 1;
  const ttk = defenderHP / Math.max(1, effectiveDPS);
  
  return { 
    ttk, 
    dps: effectiveDPS, 
    perHit: effectiveDamage,
    hitsPerSec,
    defenderHP,
  };
};

// =====================================================================
// STAT LABELS & DISPLAY HELPERS
// =====================================================================

export const STAT_LABELS = {
  hp: "HP",
  phys_atk: "ATK",
  magic_power: "AP",
  phys_def: "Phys DEF",
  phys_def_pct: "Phys DEF %",
  magic_def: "Magic DEF",
  magic_def_pct: "Magic DEF %",
  move_speed: "Move Speed",
  move_speed_pct: "Move Speed %",
  max_mana: "Max Mana",
  armor_pierce: "Armor Pierce",
  armor_pierce_pct: "Armor Pierce %",
  magic_pierce: "Magic Pierce",
  magic_pierce_pct: "Magic Pierce %",
  attack_speed: "Attack Speed",
  crit_rate: "Crit Rate",
  crit_dmg: "Crit Damage",
  life_steal: "Life Steal",
  magic_life_steal: "Magic Life Steal",
  cooldown_reduction: "CDR",
  hp_regen: "HP Regen",
  mana_regen: "Mana Regen",
  hp_pct: "HP %",
  phys_atk_pct: "ATK %",
  magic_power_pct: "AP %",
  spell_vamp: "Spell Vamp",
};

/**
 * Format stat value สำหรับแสดงผล
 * @param {string} key - stat key
 * @param {number} value - stat value
 * @returns {string} formatted string
 */
export const formatStatValue = (key, value) => {
  const v = +value || 0;
  
  // Percentage stats แสดงพร้อม %
  if (PERCENTAGE_STATS.has(key) || key.endsWith('_pct')) {
    return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
  }
  
  // Flat stats แสดงพร้อม +/-
  return `${v >= 0 ? '+' : ''}${Math.round(v).toLocaleString()}`;
};

/**
 * เช็คว่า stat นี้เป็น percentage หรือไม่
 */
export const isPercentageStat = (key) => {
  return PERCENTAGE_STATS.has(key) || key.endsWith('_pct');
};

// =====================================================================
// EXPORTS
// =====================================================================

export default {
  sumItemStatsByType,
  computeTotalStatsRealistic,
  applyDefenseWithPenetration,
  computeSkillRawDamage,
  calculateAutoDPS,
  calculateEffectiveHP,
  calculateTTK,
  STAT_LABELS,
  formatStatValue,
  isPercentageStat,
  PERCENTAGE_STATS,
  FLAT_STATS,
  PERCENTAGE_OF_BASE,
};
